import { useCallback, useState } from 'react'
import { FaEye } from 'react-icons/fa'
import { FiX } from 'react-icons/fi'

import { Modal } from '~/components/Modal'
import {
  computeDiscountValue,
  numberToBRL,
  sumTotalItemsValue,
  translatePaymentInterval,
  translatePaymentMethods,
} from '~/modules/sales/services'
import { Subscription } from '~/modules/sales/types'
import { generateUuid } from '~/utils/generateUuid'
import { getLocalDate } from '~/utils/getLocalDate'

type Props = {
  subscription: Subscription
}

export const SubscriptionDetailsButton: React.FC<Props> = ({
  subscription,
}) => {
  const [showModal, setShowModal] = useState<boolean>(false)

  const handleToggleModal = useCallback(() => {
    setShowModal((state) => !state)
  }, [])

  return (
    <>
      <button
        className='py-2 px-4 rounded-md bg-gpa-blue-500 hover:bg-blue-800 text-white'
        onClick={handleToggleModal}
      >
        <FaEye size={18} />
      </button>
      <Modal visible={showModal}>
        <div className='grid gap-4'>
          <div className='flex items-center justify-between mb-4'>
            <h1 className='text-xl'>Detalhes da assinatura</h1>
            <button
              type='button'
              className='p-2 rounded-full hover:bg-slate-50'
              onClick={handleToggleModal}
            >
              <FiX size={26} />
            </button>
          </div>

          <div className='flex flex-col gap-4 overflow-hidden'>
            <div className='flex justify-between'>
              <span className='text-lg font-bold'>
                {subscription.externalId}
              </span>
              <span className='text-lg'>
                {getLocalDate(subscription.createdAt)}
              </span>
            </div>

            <div className='flex flex-col gap-2 bg-slate-100 p-3 rounded-md'>
              <p className='font-bold text-lg'>Plano:</p>
              <div className='grid grid-cols-2 gap-3'>
                {subscription.plan?.name ? (
                  <>
                    <div className='grid grid-cols-1'>
                      <b>Nome:</b>
                      <span>{subscription.plan?.name}</span>
                    </div>
                    <div className='grid grid-cols-1'>
                      <b>Criado:</b>
                      <span>
                        {new Date(
                          subscription.plan?.createdAt || '',
                        ).toLocaleString()}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className='grid grid-cols-1'>
                    <b>{subscription.metaData?.planName}</b>
                  </div>
                )}
                <div className='grid grid-cols-1'>
                  <b>Periodicidade:</b>
                  <span>
                    {translatePaymentInterval(
                      subscription.plan?.interval ?? 'year',
                      subscription.plan?.intervalCount ?? 0,
                    )}
                  </span>
                </div>
              </div>
            </div>

            <div className='flex flex-col gap-2 bg-gpa-blue-50 p-3 rounded-md'>
              <p className='font-bold text-lg'>Cliente:</p>
              <div className='grid grid-cols-2 gap-3'>
                <div className='grid grid-cols-1'>
                  <b>Nome:</b>
                  <span>{subscription.personalTrainer.name}</span>
                </div>
                <div className='grid grid-cols-1'>
                  <b>CPF:</b>
                  <span>{subscription.personalTrainer.documentNumber}</span>
                </div>
                <div className='grid grid-cols-1'>
                  <b>E-mail:</b>
                  <span>{subscription.personalTrainer.email}</span>
                </div>
                <div className='grid grid-cols-1'>
                  <b>Telefone:</b>
                  <span>
                    {subscription.personalTrainer.phoneNumber ?? 'Sem número'}
                  </span>
                </div>
                <div className='grid grid-cols-1'>
                  <b>Assinado no dia:</b>
                  <span>
                    {new Date(subscription.createdAt).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            <div className='flex flex-col gap-2 bg-slate-100 p-3 rounded-md'>
              <p>Formas de pagamento:</p>
              <ul className='list-disc flex gap-6 pl-6'>
                {translatePaymentMethods(
                  subscription.plan?.metaData.paymentMethods ?? [],
                ).map((m, i) => (
                  <li key={`${i}_payment_method`}>{m}</li>
                ))}
              </ul>
            </div>

            <div className='flex flex-col gap-2'>
              <p>Itens da assinatura</p>
              <div className='flex w-full p-3 rounded-md border border-gpa-blue-50 overflow-x-auto'>
                <table className='table-fixed w-full bg-gpa-blue-50 min-w-[500px]'>
                  <thead>
                    <tr className='text-left'>
                      <th>Nome</th>
                      <th>Quantidade</th>
                      <th>Preço unitário</th>
                      <th>Preço total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscription.metaData.items?.map((i) => (
                      <tr
                        key={generateUuid()}
                        className='even:bg-slate-100 odd:bg-white'
                      >
                        <td>
                          {i.name} - {i.id}
                        </td>
                        <td>{i.quantity}</td>
                        <td>{numberToBRL(i.pricing_scheme.price / 100)}</td>
                        <td>
                          {numberToBRL(
                            (i.quantity * i.pricing_scheme.price) / 100,
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className='mb-4 text-right mt-1'>
                <strong>Valor total do plano: </strong>
                {numberToBRL(
                  sumTotalItemsValue(subscription.metaData.items as any) / 100,
                )}
              </p>

              {subscription.subscriptionDiscount[0]?.discount ? (
                <>
                  <p>Descontos utilizados</p>
                  <div className='flex w-full p-3 rounded-md border border-gpa-blue-50 overflow-x-auto'>
                    <table className='table-fixed w-full bg-gpa-blue-50 min-w-[500px]'>
                      <thead>
                        <tr className='text-left'>
                          <th>Código</th>
                          <th>Tipo</th>
                          <th>Valor</th>
                          <th>Ciclos</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className='even:bg-slate-100 odd:bg-white'>
                          <td>
                            {
                              subscription.subscriptionDiscount?.[0].discount
                                .code
                            }
                          </td>
                          <td>
                            {subscription.subscriptionDiscount?.[0].discount
                              .type === 'percentage'
                              ? 'Porcentagem'
                              : 'Valor Fixo'}
                          </td>
                          <td>
                            {subscription.subscriptionDiscount?.[0].discount
                              .type === 'percentage'
                              ? `${subscription.subscriptionDiscount?.[0].discount.value}%`
                              : numberToBRL(
                                  subscription.subscriptionDiscount?.[0]
                                    .discount.value / 100,
                                )}
                          </td>
                          <td>
                            {subscription.subscriptionDiscount?.[0].discount
                              .cycles ?? 1}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              ) : null}

              {subscription.subscriptionDiscount?.[0]?.discount ? (
                <p className='text-right'>
                  <strong>Desconto: </strong>
                  {numberToBRL(
                    computeDiscountValue(
                      subscription.subscriptionDiscount?.[0].discount.type,
                      subscription.subscriptionDiscount?.[0].discount.value,
                      sumTotalItemsValue(subscription.metaData.items as any),
                    ) / 100,
                  )}
                </p>
              ) : null}

              <p className='mb-4 text-right mt-1'>
                <strong>Valor Final: </strong>
                {numberToBRL(
                  (sumTotalItemsValue(subscription.metaData.items as any) -
                    (computeDiscountValue(
                      subscription.subscriptionDiscount?.[0]?.discount.type,
                      subscription.subscriptionDiscount?.[0]?.discount.value,
                      sumTotalItemsValue(subscription.metaData.items as any),
                    ) || 0)) /
                    100,
                )}
              </p>
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}
